import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import './ToastContext.css';

const ToastContext = createContext();

/**
 * useToast - app-wide, accessible toast notifications.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success('Profile saved');
 *   toast.error('Something went wrong');
 *   toast.info('Heads up');
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

let idCounter = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const show = useCallback((message, { type = 'info', duration = 4000 } = {}) => {
    if (!message) return;
    const id = ++idCounter;
    setToasts((prev) => [...prev, { id, message, type }]);
    if (duration > 0) {
      timers.current[id] = setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  const api = {
    show,
    dismiss,
    success: useCallback((msg, opts) => show(msg, { ...opts, type: 'success' }), [show]),
    error: useCallback((msg, opts) => show(msg, { ...opts, type: 'error', duration: opts?.duration ?? 6000 }), [show]),
    info: useCallback((msg, opts) => show(msg, { ...opts, type: 'info' }), [show]),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* aria-live region so screen readers announce toasts */}
      <div className="toast-container" role="region" aria-label="Notifications">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast toast--${t.type}`}
            role={t.type === 'error' ? 'alert' : 'status'}
            aria-live={t.type === 'error' ? 'assertive' : 'polite'}
          >
            <span className="toast-icon" aria-hidden="true">
              {t.type === 'success' ? '✓' : t.type === 'error' ? '!' : 'i'}
            </span>
            <span className="toast-message">{t.message}</span>
            <button
              type="button"
              className="toast-close"
              aria-label="Dismiss notification"
              onClick={() => dismiss(t.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
